/**
 * PipelineRunsDashboard — standalone "Pipeline Runs" tab for the Pipeline
 * Engineer agent, next to "Scheduler".
 *
 * Lets the user pick any pipeline in the project from a list (same pattern
 * as SchedulerTab), then shows that pipeline's run status/history via
 * <PipelineRunsTab>, decoupled from having a pipeline open in the
 * Pipelines tab.
 */

import { useCallback, useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { EmptyState } from "@/shared/components/platform/EmptyState";
import { listPipelines } from "../../features/pipeline-engineer/api/pe-client";
import type { PEPipeline } from "../../features/pipeline-engineer/types";
import { PipelineRunsTab } from "./PipelineRunsTab";

interface PipelineRunsDashboardProps {
  projectId: string;
}

export function PipelineRunsDashboard({
  projectId: _projectId,
}: PipelineRunsDashboardProps) {
  const [pipelines, setPipelines] = useState<PEPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchPipelines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPipelines();
      setPipelines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pipelines");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPipelines();
  }, [fetchPipelines]);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-8 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-white/30" />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="glass-card rounded-2xl p-4">
        <EmptyState
          variant="system-issue"
          heading="Failed to load pipelines"
          description={error}
          primaryAction={{
            label: "Retry",
            onClick: () => void fetchPipelines(),
          }}
        />
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-4">
        <EmptyState
          variant="first-visit"
          heading="No pipelines yet"
          description="Create a pipeline in the Pipelines tab to see its run history here."
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: pipeline picker */}
      <div className="lg:col-span-1 glass-card rounded-2xl p-3 space-y-1 h-fit">
        <h2 className="text-sm font-semibold text-white/80 px-2 py-1">
          Select a pipeline
        </h2>
        {pipelines.map((p) => {
          const selected = p.id === selectedId;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-xs font-medium transition-colors ${
                selected
                  ? "bg-brand-500/20 text-brand-200 border border-brand-500/30"
                  : "text-white/70 hover:bg-white/5 border border-transparent"
              }`}
            >
              <span className="truncate">{p.pipelineName}</span>
            </button>
          );
        })}
      </div>

      {/* Right: run status/history for the selected pipeline */}
      <div className="lg:col-span-2">
        {selectedId === null ? (
          <div className="glass-card rounded-2xl p-8 flex flex-col items-center justify-center text-center space-y-4 min-h-48">
            <div className="h-12 w-12 rounded-full bg-brand-500/10 flex items-center justify-center">
              <History
                size={24}
                className="text-brand-400"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-base font-semibold text-white">
              Select a pipeline
            </h3>
            <p className="text-sm text-white/50 max-w-sm">
              Choose a pipeline from the list to view its job run status and
              history.
            </p>
          </div>
        ) : (
          <PipelineRunsTab key={selectedId} pipelineId={selectedId} />
        )}
      </div>
    </div>
  );
}
